import React, { Component } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { Auth as CognitoAuth } from 'aws-amplify';
import AuthService from './AuthService';
import AuthModalContext from '../contexts/AuthModalContext';
import {
  validateEmail,
  validatePassword,
  validateName,
  validateUsername,
  validateCountry,
  getPasswordStrength,
  checkEmailUnique,
  checkUsernameUnique,
} from '../utils/authValidation';
import constants from '../config';
import mixpanel, { initMixpanel } from '../utils/mixpanelConsent';
import loginLogoLight from './assets/logo.svg';
import loginLogoDark from './assets/logo-gold.svg';

initMixpanel();

const Auth = new AuthService();

class AuthModal extends Component {
  static contextType = AuthModalContext;

  constructor(props) {
    super(props);
    this.state = {
      // Login fields
      loginEmail: '',
      loginPassword: '',
      loginEmailError: '',
      loginPasswordError: '',
      loginLoading: false,

      // Signup fields
      signupName: '',
      signupUsername: '',
      signupEmail: '',
      signupPassword: '',
      signupCountry: 'United States',
      signupCorrectAge: false,
      signupAcceptTerms: false,
      signupNameError: '',
      signupUsernameError: '',
      signupEmailError: '',
      signupPasswordError: '',
      signupCountryError: '',
      signupAgeError: '',
      signupTermsError: '',
      signupLoading: false,

      // Real-time feedback
      emailAvailable: null,
      usernameAvailable: null,
      passwordStrength: null,

      // Forgot password
      forgotView: false,
      forgotEmail: '',
      forgotLoading: false,
      forgotSuccess: false,
      forgotError: '',

      // Forgot password — code-based flow
      forgotCodeView: false,
      forgotCode: '',
      forgotNewPassword: '',
      forgotNewPasswordError: '',
      forgotCodeError: '',
      forgotResetLoading: false,

      // Password visibility
      showLoginPassword: false,
      showSignupPassword: false,
      showForgotNewPassword: false,

      // Primal community welcome
      primalWelcome: null,
      showPrimalWelcome: false,
      isDark: document.documentElement.classList.contains('tw-dark'),

      // Confirmation code (signup)
      confirmationView: false,
      confirmationCode: '',
      confirmationError: '',
      confirmationLoading: false,
      pendingSignup: null,
    };

    this.handleLoginSubmit = this.handleLoginSubmit.bind(this);
    this.handleSignupSubmit = this.handleSignupSubmit.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleOverlayMouseDown = this.handleOverlayMouseDown.bind(this);
    this._mouseDownTarget = null;
    this._darkObserver = null;
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    var modal = this;
    this._darkObserver = new MutationObserver(function() {
      var dark = document.documentElement.classList.contains('tw-dark');
      if (dark !== modal.state.isDark) modal.setState({ isDark: dark });
    });
    this._darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    if (this._darkObserver) this._darkObserver.disconnect();
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' && this.context.isOpen) {
      this.context.closeAuthModal();
    }
  }

  handleOverlayMouseDown(e) {
    this._mouseDownTarget = e.target;
  }

  handleOverlayClick(e) {
    if (e.target === e.currentTarget && this._mouseDownTarget === e.currentTarget) {
      this.context.closeAuthModal();
    }
  }

  resetErrors() {
    this.setState({
      loginEmailError: '',
      loginPasswordError: '',
      signupNameError: '',
      signupUsernameError: '',
      signupEmailError: '',
      signupPasswordError: '',
      signupCountryError: '',
      signupAgeError: '',
      signupTermsError: '',
    });
  }

  handleTabSwitch(tab) {
    this.resetErrors();
    this.setState({
      forgotView: false, forgotSuccess: false, forgotError: '',
      forgotCodeView: false, forgotCode: '', forgotNewPassword: '', forgotCodeError: '', forgotNewPasswordError: '',
      confirmationView: false, confirmationCode: '', confirmationError: '', pendingSignup: null,
    });
    this.context.openAuthModal(tab, this.context.onSuccessCallback);
  }

  onAuthSuccess() {
    var onSuccessCallback = this.context.onSuccessCallback;
    var closeAuthModal = this.context.closeAuthModal;
    closeAuthModal();
    window.dispatchEvent(new Event('authStateChanged'));
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  }

  // Login via Cognito
  handleLoginSubmit(e) {
    e.preventDefault();
    var loginEmail = this.state.loginEmail;
    var loginPassword = this.state.loginPassword;
    var email = loginEmail.toLowerCase().trim();
    var password = loginPassword;

    var emailErr = validateEmail(email);
    if (emailErr) {
      this.setState({ loginEmailError: emailErr });
      return;
    }
    if (!password) {
      this.setState({ loginPasswordError: 'Password is required' });
      return;
    }

    this.setState({ loginLoading: true, loginEmailError: '', loginPasswordError: '' });

    var modal = this;
    Auth.login(email, password)
      .then(function() {
        modal.setState({ loginLoading: false });
        modal.onAuthSuccess();
      })
      .catch(function(err) {
        var msg = 'Invalid email or password. Please try again.';
        if (err.code === 'UserNotConfirmedException' || err.name === 'UserNotConfirmedException') {
          msg = 'Please verify your email first. Check your inbox for a confirmation code.';
        } else if (err.code === 'NotAuthorizedException' || err.name === 'NotAuthorizedException') {
          msg = 'Invalid email or password. Please try again.';
        } else if (err.message) {
          msg = err.message;
        }
        modal.setState({ loginLoading: false, loginPasswordError: msg });
      });
  }

  // Signup via Cognito
  handleSignupSubmit(e) {
    e.preventDefault();
    var signupName = this.state.signupName;
    var signupUsername = this.state.signupUsername;
    var signupEmail = this.state.signupEmail;
    var signupPassword = this.state.signupPassword;
    var signupCountry = this.state.signupCountry;
    var signupCorrectAge = this.state.signupCorrectAge;
    var signupAcceptTerms = this.state.signupAcceptTerms;

    var name = signupName.trim();
    var userName = signupUsername.trim();
    var email = signupEmail.toLowerCase().trim();
    var password = signupPassword;
    var country = signupCountry;

    var nameErr = validateName(name);
    if (nameErr) { this.setState({ signupNameError: nameErr }); return; }

    var usernameErr = validateUsername(userName);
    if (usernameErr) { this.setState({ signupUsernameError: usernameErr }); return; }

    var emailErr = validateEmail(email);
    if (emailErr) { this.setState({ signupEmailError: emailErr }); return; }

    var countryErr = validateCountry(country);
    if (countryErr) { this.setState({ signupCountryError: countryErr }); return; }

    var passwordErr = validatePassword(password);
    if (passwordErr) { this.setState({ signupPasswordError: passwordErr }); return; }

    if (!signupCorrectAge) {
      this.setState({ signupAgeError: 'Must be over 13 to register' });
      return;
    }

    if (!signupAcceptTerms) {
      this.setState({ signupTermsError: 'Please accept the terms and conditions' });
      return;
    }

    this.setState({
      signupLoading: true,
      signupNameError: '', signupUsernameError: '', signupEmailError: '',
      signupPasswordError: '', signupCountryError: '', signupAgeError: '', signupTermsError: '',
    });

    var modal = this;
    Auth.signup(name, userName, email, password, country)
      .then(function(result) {
        modal.setState({ signupLoading: false });
        if (result.needsConfirmation) {
          modal.setState({
            confirmationView: true,
            pendingSignup: {
              cognitoSub: result.cognitoSub,
              name: name,
              userName: userName,
              email: email,
              password: password,
              country: country,
            },
          });
        } else {
          // Auto-confirmed (shouldn't happen with email verification, but handle it)
          modal._completeSignup(result.cognitoSub, name, userName, email, password, country);
        }
      })
      .catch(function(err) {
        var msg = 'Signup failed. Please try again.';
        if (err.code === 'UsernameExistsException' || err.name === 'UsernameExistsException') {
          msg = 'This username or email is already taken.';
        } else if (err.code === 'InvalidPasswordException' || err.name === 'InvalidPasswordException') {
          msg = err.message || 'Password does not meet requirements.';
        } else if (err.message) {
          msg = err.message;
        }
        modal.setState({ signupLoading: false, signupEmailError: msg });
      });
  }

  // Handle confirmation code submission
  handleConfirmSubmit(e) {
    e.preventDefault();
    var code = this.state.confirmationCode.trim();
    var pending = this.state.pendingSignup;
    if (!code) {
      this.setState({ confirmationError: 'Please enter the confirmation code' });
      return;
    }
    if (!pending) return;

    this.setState({ confirmationLoading: true, confirmationError: '' });

    var modal = this;
    Auth.confirmSignUp(pending.email, code)
      .then(function() {
        return modal._completeSignup(pending.cognitoSub, pending.name, pending.userName, pending.email, pending.password, pending.country);
      })
      .catch(function(err) {
        var msg = 'Invalid code. Please try again.';
        if (err.code === 'CodeMismatchException' || err.name === 'CodeMismatchException') {
          msg = 'Invalid verification code. Please check and try again.';
        } else if (err.code === 'ExpiredCodeException' || err.name === 'ExpiredCodeException') {
          msg = 'Code has expired. Please request a new one.';
        } else if (err.message) {
          msg = err.message;
        }
        modal.setState({ confirmationLoading: false, confirmationError: msg });
      });
  }

  // After confirmation, sign into Cognito, create MongoDB user, cache profile
  _completeSignup(cognitoSub, name, userName, email, password, country) {
    var modal = this;
    // Step 1: Sign into Cognito to get the ID token (don't use Auth.login which calls /me)
    return CognitoAuth.signIn(email.toLowerCase().trim(), password)
      .then(function(user) {
        var idToken = user.signInUserSession.idToken.jwtToken;
        localStorage.setItem('primal_cognito_token', idToken);
        // Step 2: Create MongoDB user — this must happen BEFORE any /me call
        return Auth.createMongoUser(cognitoSub, name, userName, email, country, idToken);
      })
      .then(function(profile) {
        // Step 3: Cache the profile from cognito-signup response
        localStorage.setItem('primal_user_profile', JSON.stringify(profile));
        mixpanel.identify(userName);
        mixpanel.track('NewUser');
        modal.setState({ confirmationLoading: false, confirmationView: false, pendingSignup: null });
        modal.onAuthSuccess();
      })
      .catch(function(err) {
        modal.setState({ confirmationLoading: false, confirmationError: err.message || 'Failed to complete signup' });
      });
  }

  handleResendCode() {
    var pending = this.state.pendingSignup;
    if (!pending) return;
    var modal = this;
    Auth.resendSignUp(pending.email)
      .then(function() {
        modal.setState({ confirmationError: '' });
        // Brief success indication
        modal.setState({ confirmationError: 'A new code has been sent to your email.' });
        setTimeout(function() {
          if (modal.state.confirmationError === 'A new code has been sent to your email.') {
            modal.setState({ confirmationError: '' });
          }
        }, 3000);
      })
      .catch(function() {
        modal.setState({ confirmationError: 'Failed to resend code. Please try again.' });
      });
  }

  // Real-time validation handlers
  handleSignupEmailChange(email) {
    this.setState({ signupEmail: email, emailAvailable: null });
    if (validateEmail(email) === null) {
      checkEmailUnique(email).then(function(available) {
        if (this.state.signupEmail === email) {
          this.setState({ emailAvailable: available });
        }
      }.bind(this));
    }
  }

  handleSignupUsernameChange(username) {
    this.setState({ signupUsername: username, usernameAvailable: null });
    if (username.trim()) {
      checkUsernameUnique(username).then(function(available) {
        if (this.state.signupUsername === username) {
          this.setState({ usernameAvailable: available });
        }
      }.bind(this));
    }
  }

  handleSignupPasswordChange(password) {
    this.setState({
      signupPassword: password,
      passwordStrength: getPasswordStrength(password),
    });
  }

  dismissPrimalWelcome() {
    this.setState({ showPrimalWelcome: false, primalWelcome: null });
    this.onAuthSuccess();
  }

  // Forgot password — step 1: request code
  handleForgotSubmit(e) {
    e.preventDefault();
    var email = this.state.forgotEmail.toLowerCase().trim();
    if (!email) {
      this.setState({ forgotError: 'Please enter your email address' });
      return;
    }
    this.setState({ forgotLoading: true, forgotError: '' });
    var modal = this;
    Auth.forgotPassword(email)
      .then(function() {
        modal.setState({ forgotLoading: false, forgotCodeView: true, forgotError: '' });
      })
      .catch(function(err) {
        var msg = 'Something went wrong. Please try again.';
        if (err.code === 'UserNotFoundException' || err.name === 'UserNotFoundException') {
          // Don't reveal if email exists — show generic success
          modal.setState({ forgotLoading: false, forgotCodeView: true, forgotError: '' });
          return;
        }
        if (err.code === 'LimitExceededException' || err.name === 'LimitExceededException') {
          msg = 'Too many attempts. Please try again later.';
        }
        modal.setState({ forgotLoading: false, forgotError: msg });
      });
  }

  // Forgot password — step 2: submit code + new password
  handleForgotCodeSubmit(e) {
    e.preventDefault();
    var email = this.state.forgotEmail.toLowerCase().trim();
    var code = this.state.forgotCode.trim();
    var newPassword = this.state.forgotNewPassword;

    if (!code) {
      this.setState({ forgotCodeError: 'Please enter the verification code' });
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      this.setState({ forgotNewPasswordError: 'Password must be at least 8 characters' });
      return;
    }

    this.setState({ forgotResetLoading: true, forgotCodeError: '', forgotNewPasswordError: '' });

    var modal = this;
    Auth.forgotPasswordSubmit(email, code, newPassword)
      .then(function() {
        modal.setState({ forgotResetLoading: false, forgotSuccess: true, forgotCodeView: false });
      })
      .catch(function(err) {
        var msg = 'Failed to reset password. Please try again.';
        if (err.code === 'CodeMismatchException' || err.name === 'CodeMismatchException') {
          msg = 'Invalid verification code. Please check and try again.';
        } else if (err.code === 'ExpiredCodeException' || err.name === 'ExpiredCodeException') {
          msg = 'Code has expired. Please request a new one.';
        } else if (err.code === 'InvalidPasswordException' || err.name === 'InvalidPasswordException') {
          modal.setState({ forgotResetLoading: false, forgotNewPasswordError: err.message || 'Password does not meet requirements.' });
          return;
        }
        modal.setState({ forgotResetLoading: false, forgotCodeError: msg });
      });
  }

  renderPrimalWelcome() {
    var primalWelcome = this.state.primalWelcome;
    if (!primalWelcome) return null;

    var PRIMAL_API = 'https://api.primaltcg.com';
    var avatarUrl = primalWelcome.image ? (PRIMAL_API + '/' + primalWelcome.image) : loginLogoDark;
    var displayName = primalWelcome.name || 'Trainer';

    var confettiColors = ['#d4a843', '#f5d47a', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96e6a1', '#dda0dd', '#f0e68c'];
    var confetti = [];
    for (var i = 0; i < 50; i++) {
      var color = confettiColors[i % confettiColors.length];
      var left = Math.random() * 100;
      var delay = Math.random() * 2;
      var duration = 2.5 + Math.random() * 2;
      var size = 6 + Math.random() * 6;
      var rotation = Math.random() * 360;
      var shape = i % 3;
      confetti.push(
        <div
          key={'confetti-' + i}
          style={{
            position: 'absolute',
            top: '-10px',
            left: left + '%',
            width: shape === 2 ? size * 0.5 : size,
            height: size,
            backgroundColor: color,
            borderRadius: shape === 1 ? '50%' : '2px',
            opacity: 0,
            transform: 'rotate(' + rotation + 'deg)',
            animation: 'primalConfettiFall ' + duration + 's ease-out ' + delay + 's forwards',
          }}
        />
      );
    }

    var modal = this;

    return (
      <div
        className="tw-fixed tw-inset-0 tw-z-[10000] tw-flex tw-items-center tw-justify-center tw-p-4"
        style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
      >
        <style>{'\
          @keyframes primalConfettiFall {\
            0% { opacity: 1; transform: translateY(0) rotate(0deg) scale(1); }\
            50% { opacity: 1; }\
            100% { opacity: 0; transform: translateY(calc(100vh + 20px)) rotate(720deg) scale(0.5); }\
          }\
          @keyframes primalCardEntrance {\
            0% { opacity: 0; transform: scale(0.5) translateY(30px); }\
            50% { opacity: 1; transform: scale(1.03) translateY(-5px); }\
            100% { opacity: 1; transform: scale(1) translateY(0); }\
          }\
          @keyframes primalAvatarPop {\
            0% { opacity: 0; transform: scale(0); }\
            60% { transform: scale(1.15); }\
            100% { opacity: 1; transform: scale(1); }\
          }\
          @keyframes primalGlow {\
            0%, 100% { box-shadow: 0 0 8px rgba(212, 168, 67, 0.4), 0 0 20px rgba(212, 168, 67, 0.1); }\
            50% { box-shadow: 0 0 16px rgba(212, 168, 67, 0.6), 0 0 40px rgba(212, 168, 67, 0.2); }\
          }\
          @keyframes primalTextFade {\
            0% { opacity: 0; transform: translateY(10px); }\
            100% { opacity: 1; transform: translateY(0); }\
          }\
          @keyframes primalShimmer {\
            0% { background-position: -200% center; }\
            100% { background-position: 200% center; }\
          }\
          @keyframes primalSparkle {\
            0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }\
            50% { opacity: 1; transform: scale(1) rotate(180deg); }\
          }\
        '}</style>

        <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 10001 }}>
          {confetti}
        </div>

        <div
          className="tw-bg-white dark:tw-bg-[#1a1a2e] tw-rounded-2xl tw-p-8 tw-max-w-sm tw-w-full tw-text-center tw-relative tw-overflow-hidden"
          style={{
            animation: 'primalCardEntrance 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
            opacity: 0,
            boxShadow: '0 25px 60px rgba(0,0,0,0.3), 0 0 40px rgba(212, 168, 67, 0.1)',
            zIndex: 10002,
          }}
        >
          {[
            { top: '12%', left: '8%', delay: '0.8s', size: 14 },
            { top: '8%', right: '10%', delay: '1.2s', size: 10 },
            { bottom: '20%', left: '6%', delay: '1.6s', size: 12 },
            { bottom: '15%', right: '8%', delay: '2s', size: 8 },
            { top: '40%', left: '3%', delay: '2.4s', size: 10 },
            { top: '35%', right: '4%', delay: '1s', size: 12 },
          ].map(function(s, idx) {
            return (
              <div
                key={'sparkle-' + idx}
                style={{
                  position: 'absolute',
                  top: s.top, left: s.left, right: s.right, bottom: s.bottom,
                  width: s.size, height: s.size,
                  color: '#d4a843',
                  fontSize: s.size,
                  animation: 'primalSparkle 1.5s ease-in-out ' + s.delay + ' infinite',
                  opacity: 0,
                  pointerEvents: 'none',
                }}
              >
                &#10022;
              </div>
            );
          })}

          <div className="tw-flex tw-justify-center tw-mb-5">
            <div
              className="tw-rounded-full tw-overflow-hidden tw-flex tw-items-center tw-justify-center"
              style={{
                width: '90px',
                height: '90px',
                border: '3px solid #d4a843',
                animation: 'primalAvatarPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s forwards, primalGlow 2s ease-in-out 1s infinite',
                opacity: 0,
              }}
            >
              <img
                src={avatarUrl}
                alt={displayName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={function(e) { e.target.src = loginLogoDark; }}
              />
            </div>
          </div>

          <h3
            className="tw-text-xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1 tw-mt-0"
            style={{ animation: 'primalTextFade 0.4s ease-out 0.6s forwards', opacity: 0 }}
          >
            {displayName}
          </h3>

          <p
            className="tw-text-xs tw-font-semibold tw-mb-4 tw-mt-0"
            style={{
              animation: 'primalTextFade 0.4s ease-out 0.75s forwards',
              opacity: 0,
              color: '#d4a843',
              letterSpacing: '1px',
              textTransform: 'uppercase',
            }}
          >
            Primal Community Member
          </p>

          <p
            className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-6 tw-leading-relaxed"
            style={{ animation: 'primalTextFade 0.4s ease-out 0.9s forwards', opacity: 0 }}
          >
            Hey {displayName}! We see you're already part of the Primal community. Welcome to the Marketplace!
          </p>

          <button
            onClick={function() { modal.dismissPrimalWelcome(); }}
            className="tw-w-full tw-py-3.5 tw-rounded-lg tw-text-black tw-font-bold tw-text-sm tw-border-0 tw-cursor-pointer tw-transition-all"
            style={{
              animation: 'primalTextFade 0.4s ease-out 1.1s forwards',
              opacity: 0,
              background: 'linear-gradient(90deg, #d4a843, #f5d47a, #d4a843)',
              backgroundSize: '200% auto',
            }}
            onMouseEnter={function(e) { e.target.style.animation = 'primalShimmer 1.5s linear infinite'; e.target.style.opacity = '1'; }}
            onMouseLeave={function(e) { e.target.style.animation = 'none'; e.target.style.opacity = '1'; }}
          >
            Let's Go!
          </button>
        </div>
      </div>
    );
  }

  renderConfirmationView() {
    var confirmationCode = this.state.confirmationCode;
    var confirmationError = this.state.confirmationError;
    var confirmationLoading = this.state.confirmationLoading;
    var pending = this.state.pendingSignup;
    var modal = this;

    return (
      <div>
        <div className="tw-text-center tw-mb-5">
          <div className="tw-text-4xl tw-mb-3" style={{ color: '#d4a843' }}>
            <i className="fa fa-envelope" />
          </div>
          <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2">Verify Your Email</h3>
          <p className="tw-text-[#666] dark:tw-text-gray-400 tw-text-sm">
            We sent a 6-digit code to <strong>{pending ? pending.email : ''}</strong>. Enter it below to complete your registration.
          </p>
        </div>

        {confirmationError && (
          <p className={'tw-text-xs tw-mb-3 ' + (confirmationError.indexOf('new code') > -1 ? 'tw-text-green-500' : 'tw-text-red-500')}>
            {confirmationError}
          </p>
        )}

        <form onSubmit={function(e) { modal.handleConfirmSubmit(e); }}>
          <div className="tw-mb-4">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-[#333] dark:tw-text-gray-300 tw-mb-1.5">
              Confirmation Code
            </label>
            <input
              type="text"
              value={confirmationCode}
              onChange={function(e) { modal.setState({ confirmationCode: e.target.value, confirmationError: '' }); }}
              placeholder="Enter 6-digit code"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors tw-text-center tw-tracking-widest tw-text-lg tw-font-mono"
              style={{ fontSize: '20px', letterSpacing: '0.3em' }}
            />
          </div>
          <button
            type="submit"
            disabled={confirmationLoading}
            className="tw-w-full tw-py-3 tw-rounded-lg tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-text-sm tw-border-0 tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {confirmationLoading ? 'Verifying...' : 'Verify & Create Account'}
          </button>
        </form>

        <p className="tw-text-center tw-text-sm tw-text-[#999] dark:tw-text-gray-400 tw-mt-4 tw-mb-0">
          Didn't receive a code?{' '}
          <button
            onClick={function() { modal.handleResendCode(); }}
            className="tw-text-brand-gold hover:tw-text-brand-gold-dark tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-sm tw-font-semibold tw-p-0"
          >
            Resend Code
          </button>
        </p>
      </div>
    );
  }

  renderForgotView() {
    var forgotEmail = this.state.forgotEmail;
    var forgotLoading = this.state.forgotLoading;
    var forgotSuccess = this.state.forgotSuccess;
    var forgotError = this.state.forgotError;
    var forgotCodeView = this.state.forgotCodeView;
    var modal = this;

    if (forgotSuccess) {
      return (
        <div className="tw-text-center">
          <div className="tw-text-4xl tw-mb-4" style={{ color: '#10b981' }}>
            <i className="fa fa-check-circle" />
          </div>
          <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2">Password Reset!</h3>
          <p className="tw-text-[#666] dark:tw-text-gray-400 tw-text-sm tw-mb-6">
            Your password has been successfully reset. You can now sign in with your new password.
          </p>
          <button
            onClick={function() { modal.setState({ forgotView: false, forgotSuccess: false, forgotCodeView: false, forgotCode: '', forgotNewPassword: '' }); }}
            className="tw-text-brand-gold hover:tw-text-brand-gold-dark tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-sm tw-font-semibold tw-p-0"
          >
            Back to Sign In
          </button>
        </div>
      );
    }

    if (forgotCodeView) {
      return this.renderForgotCodeView();
    }

    return (
      <div>
        <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2 tw-text-center">
          Reset Password
        </h3>
        <p className="tw-text-[#666] dark:tw-text-gray-400 tw-text-sm tw-mb-4 tw-text-center">
          Enter your email and we'll send you a verification code to reset your password.
        </p>

        {forgotError && (
          <p className="tw-text-red-500 tw-text-xs tw-mb-3">{forgotError}</p>
        )}

        <form onSubmit={function(e) { modal.handleForgotSubmit(e); }}>
          <div className="tw-mb-4">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-[#333] dark:tw-text-gray-300 tw-mb-1.5">
              Email Address
            </label>
            <input
              type="email"
              value={forgotEmail}
              onChange={function(e) { modal.setState({ forgotEmail: e.target.value, forgotError: '' }); }}
              placeholder="Enter your email"
              autoComplete="email"
              autoFocus
              className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors"
              style={{ fontSize: '16px' }}
            />
          </div>
          <button
            type="submit"
            disabled={forgotLoading}
            className="tw-w-full tw-py-3 tw-rounded-lg tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-text-sm tw-border-0 tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {forgotLoading ? 'Sending...' : 'Send Verification Code'}
          </button>
        </form>

        <p className="tw-text-center tw-text-sm tw-text-[#999] dark:tw-text-gray-400 tw-mt-4 tw-mb-0">
          <button
            onClick={function() { modal.setState({ forgotView: false }); }}
            className="tw-text-brand-gold hover:tw-text-brand-gold-dark tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-sm tw-font-semibold tw-p-0"
          >
            Back to Sign In
          </button>
        </p>
      </div>
    );
  }

  renderForgotCodeView() {
    var forgotCode = this.state.forgotCode;
    var forgotNewPassword = this.state.forgotNewPassword;
    var forgotCodeError = this.state.forgotCodeError;
    var forgotNewPasswordError = this.state.forgotNewPasswordError;
    var forgotResetLoading = this.state.forgotResetLoading;
    var forgotEmail = this.state.forgotEmail;
    var modal = this;

    return (
      <div>
        <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2 tw-text-center">
          Enter Verification Code
        </h3>
        <p className="tw-text-[#666] dark:tw-text-gray-400 tw-text-sm tw-mb-4 tw-text-center">
          We sent a code to <strong>{forgotEmail}</strong>. Enter it below with your new password.
        </p>

        <form onSubmit={function(e) { modal.handleForgotCodeSubmit(e); }}>
          <div className="tw-mb-4">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-[#333] dark:tw-text-gray-300 tw-mb-1.5">
              Verification Code
            </label>
            <input
              type="text"
              value={forgotCode}
              onChange={function(e) { modal.setState({ forgotCode: e.target.value, forgotCodeError: '' }); }}
              placeholder="Enter 6-digit code"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors tw-text-center tw-tracking-widest tw-font-mono"
              style={{ fontSize: '20px', letterSpacing: '0.3em' }}
            />
            {forgotCodeError && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{forgotCodeError}</p>}
          </div>

          <div className="tw-mb-4">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-[#333] dark:tw-text-gray-300 tw-mb-1.5">
              New Password
            </label>
            <div className="tw-relative">
              <input
                type={this.state.showForgotNewPassword ? 'text' : 'password'}
                value={forgotNewPassword}
                onChange={function(e) { modal.setState({ forgotNewPassword: e.target.value, forgotNewPasswordError: '' }); }}
                placeholder="Enter new password"
                autoComplete="new-password"
                className="tw-w-full tw-px-4 tw-py-2.5 tw-pr-10 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors"
                style={{ fontSize: '16px' }}
              />
              <button
                type="button"
                onClick={function() { modal.setState(function(prev) { return { showForgotNewPassword: !prev.showForgotNewPassword }; }); }}
                className="tw-absolute tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-text-[#999] hover:tw-text-[#666] dark:hover:tw-text-gray-300 tw-transition-colors"
                style={{ right: '12px', top: '50%', transform: 'translateY(-50%)' }}
                tabIndex={-1}
              >
                <i className={'fa ' + (this.state.showForgotNewPassword ? 'fa-eye-slash' : 'fa-eye')} style={{ fontSize: '14px', lineHeight: '1', display: 'block' }} />
              </button>
            </div>
            {forgotNewPasswordError && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{forgotNewPasswordError}</p>}
          </div>

          <button
            type="submit"
            disabled={forgotResetLoading}
            className="tw-w-full tw-py-3 tw-rounded-lg tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-text-sm tw-border-0 tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {forgotResetLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <p className="tw-text-center tw-text-sm tw-text-[#999] dark:tw-text-gray-400 tw-mt-4 tw-mb-0">
          <button
            onClick={function() { modal.setState({ forgotCodeView: false }); }}
            className="tw-text-brand-gold hover:tw-text-brand-gold-dark tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-sm tw-font-semibold tw-p-0"
          >
            Back
          </button>
        </p>
      </div>
    );
  }

  renderAvailabilityIcon(available) {
    if (available === null) return null;
    if (available) {
      return <i className="fa fa-check" style={{ color: '#10b981', fontSize: '14px' }} />;
    }
    return <i className="fa fa-times" style={{ color: '#ef4444', fontSize: '14px' }} />;
  }

  renderLoginForm() {
    var loginEmail = this.state.loginEmail;
    var loginPassword = this.state.loginPassword;
    var loginEmailError = this.state.loginEmailError;
    var loginPasswordError = this.state.loginPasswordError;
    var loginLoading = this.state.loginLoading;
    var modal = this;

    return (
      <form onSubmit={this.handleLoginSubmit}>
        <div className="tw-mb-4">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-[#333] dark:tw-text-gray-300 tw-mb-1.5">
            Email Address
          </label>
          <input
            type="email"
            value={loginEmail}
            onChange={function(e) { modal.setState({ loginEmail: e.target.value, loginEmailError: '' }); }}
            placeholder="Enter your email"
            autoComplete="email"
            autoFocus
            className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors"
            style={{ fontSize: '16px' }}
          />
          {loginEmailError && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{loginEmailError}</p>}
        </div>

        <div className="tw-mb-4">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-[#333] dark:tw-text-gray-300 tw-mb-1.5">
            Password
          </label>
          <div className="tw-relative">
            <input
              type={this.state.showLoginPassword ? 'text' : 'password'}
              value={loginPassword}
              onChange={function(e) { modal.setState({ loginPassword: e.target.value, loginPasswordError: '' }); }}
              placeholder="Enter your password"
              autoComplete="current-password"
              className="tw-w-full tw-px-4 tw-py-2.5 tw-pr-10 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors"
              style={{ fontSize: '16px' }}
            />
            <button
              type="button"
              onClick={function() { modal.setState(function(prev) { return { showLoginPassword: !prev.showLoginPassword }; }); }}
              className="tw-absolute tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-text-[#999] hover:tw-text-[#666] dark:hover:tw-text-gray-300 tw-transition-colors"
              style={{ right: '12px', top: '50%', transform: 'translateY(-50%)' }}
              tabIndex={-1}
            >
              <i className={'fa ' + (this.state.showLoginPassword ? 'fa-eye-slash' : 'fa-eye')} style={{ fontSize: '14px', lineHeight: '1', display: 'block' }} />
            </button>
          </div>
          {loginPasswordError && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{loginPasswordError}</p>}
          <div className="tw-text-right tw-mt-1">
            <button
              type="button"
              onClick={function() { modal.setState({ forgotView: true, forgotSuccess: false, forgotError: '', forgotEmail: '', forgotCodeView: false, forgotCode: '', forgotNewPassword: '' }); }}
              className="tw-text-brand-gold hover:tw-text-brand-gold-dark tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-xs tw-p-0"
            >
              Forgot password?
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loginLoading}
          className="tw-w-full tw-py-3 tw-rounded-lg tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-text-sm tw-border-0 tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-mt-2"
        >
          {loginLoading ? 'Signing In...' : 'Sign In'}
        </button>
      </form>
    );
  }

  renderSignupForm() {
    var signupName = this.state.signupName;
    var signupUsername = this.state.signupUsername;
    var signupEmail = this.state.signupEmail;
    var signupPassword = this.state.signupPassword;
    var signupCountry = this.state.signupCountry;
    var signupCorrectAge = this.state.signupCorrectAge;
    var signupAcceptTerms = this.state.signupAcceptTerms;
    var signupNameError = this.state.signupNameError;
    var signupUsernameError = this.state.signupUsernameError;
    var signupEmailError = this.state.signupEmailError;
    var signupPasswordError = this.state.signupPasswordError;
    var signupCountryError = this.state.signupCountryError;
    var signupAgeError = this.state.signupAgeError;
    var signupTermsError = this.state.signupTermsError;
    var signupLoading = this.state.signupLoading;
    var emailAvailable = this.state.emailAvailable;
    var usernameAvailable = this.state.usernameAvailable;
    var passwordStrength = this.state.passwordStrength;
    var modal = this;

    return (
      <form onSubmit={this.handleSignupSubmit}>
        {/* Name */}
        <div className="tw-mb-3">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-[#333] dark:tw-text-gray-300 tw-mb-1.5">
            Full Name
          </label>
          <input
            type="text"
            value={signupName}
            onChange={function(e) { modal.setState({ signupName: e.target.value, signupNameError: '' }); }}
            placeholder="Enter your full name"
            autoComplete="name"
            className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors"
            style={{ fontSize: '16px' }}
          />
          {signupNameError && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{signupNameError}</p>}
        </div>

        {/* Username */}
        <div className="tw-mb-3">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-[#333] dark:tw-text-gray-300 tw-mb-1.5">
            Username
          </label>
          <div className="tw-relative">
            <input
              type="text"
              value={signupUsername}
              onChange={function(e) { modal.handleSignupUsernameChange(e.target.value); }}
              placeholder="Choose a username"
              autoComplete="username"
              className="tw-w-full tw-px-4 tw-py-2.5 tw-pr-10 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors"
              style={{ fontSize: '16px' }}
            />
            <span className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2">
              {this.renderAvailabilityIcon(usernameAvailable)}
            </span>
          </div>
          {signupUsernameError && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{signupUsernameError}</p>}
        </div>

        {/* Email */}
        <div className="tw-mb-3">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-[#333] dark:tw-text-gray-300 tw-mb-1.5">
            Email Address
          </label>
          <div className="tw-relative">
            <input
              type="email"
              value={signupEmail}
              onChange={function(e) { modal.handleSignupEmailChange(e.target.value); }}
              placeholder="Enter your email"
              autoComplete="email"
              className="tw-w-full tw-px-4 tw-py-2.5 tw-pr-10 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors"
              style={{ fontSize: '16px' }}
            />
            <span className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2">
              {this.renderAvailabilityIcon(emailAvailable)}
            </span>
          </div>
          {signupEmailError && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{signupEmailError}</p>}
        </div>

        {/* Password */}
        <div className="tw-mb-3">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-[#333] dark:tw-text-gray-300 tw-mb-1.5">
            Password
          </label>
          <div className="tw-relative">
            <input
              type={this.state.showSignupPassword ? 'text' : 'password'}
              value={signupPassword}
              onChange={function(e) { modal.handleSignupPasswordChange(e.target.value); }}
              placeholder="Create a strong password"
              autoComplete="new-password"
              className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors"
              style={{ fontSize: '16px', paddingRight: passwordStrength ? '52px' : '36px' }}
            />
            <button
              type="button"
              onClick={function() { modal.setState(function(prev) { return { showSignupPassword: !prev.showSignupPassword }; }); }}
              className="tw-absolute tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-text-[#999] hover:tw-text-[#666] dark:hover:tw-text-gray-300 tw-transition-colors"
              style={{ right: passwordStrength ? '28px' : '12px', top: '50%', transform: 'translateY(-50%)' }}
              tabIndex={-1}
            >
              <i className={'fa ' + (this.state.showSignupPassword ? 'fa-eye-slash' : 'fa-eye')} style={{ fontSize: '14px', lineHeight: '1', display: 'block' }} />
            </button>
            {passwordStrength && (
              <span className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2">
                {passwordStrength.valid ? (
                  <i className="fa fa-check" style={{ color: '#10b981', fontSize: '14px' }} />
                ) : (
                  <i className="fa fa-times" style={{ color: '#ef4444', fontSize: '14px' }} />
                )}
              </span>
            )}
          </div>
          {signupPasswordError && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{signupPasswordError}</p>}
          {passwordStrength && (
            <p className="tw-text-xs tw-mt-1 tw-mb-0" style={{ color: passwordStrength.color }}>
              {passwordStrength.label}
            </p>
          )}
        </div>

        {/* Country */}
        <div className="tw-mb-3">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-[#333] dark:tw-text-gray-300 tw-mb-1.5">
            Country
          </label>
          <select
            value={signupCountry}
            onChange={function(e) { modal.setState({ signupCountry: e.target.value, signupCountryError: '' }); }}
            className="tw-w-full tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors tw-appearance-none"
            style={{ fontSize: '16px' }}
          >
            <option value="United States">United States</option>
          </select>
          {signupCountryError && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{signupCountryError}</p>}
        </div>

        {/* Age checkbox */}
        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
          <input
            type="checkbox"
            id="auth-modal-age"
            checked={signupCorrectAge}
            onChange={function() { modal.setState({ signupCorrectAge: !signupCorrectAge, signupAgeError: '' }); }}
            className="tw-w-5 tw-h-5 tw-flex-shrink-0 tw-accent-brand-gold tw-cursor-pointer"
          />
          <label htmlFor="auth-modal-age" className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-0 tw-leading-normal tw-cursor-pointer">
            I am 13 years old or older
          </label>
        </div>
        {signupAgeError && <p className="tw-text-red-500 tw-text-xs tw-mb-2">{signupAgeError}</p>}

        {/* Terms checkbox */}
        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
          <input
            type="checkbox"
            id="auth-modal-terms"
            checked={signupAcceptTerms}
            onChange={function() { modal.setState({ signupAcceptTerms: !signupAcceptTerms, signupTermsError: '' }); }}
            className="tw-w-5 tw-h-5 tw-flex-shrink-0 tw-accent-brand-gold tw-cursor-pointer"
          />
          <label htmlFor="auth-modal-terms" className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-0 tw-leading-normal tw-cursor-pointer">
            I accept the <Link to="/terms" onClick={function() { modal.context.closeAuthModal(); }} className="tw-text-brand-gold hover:tw-text-brand-gold-dark tw-no-underline">terms and conditions</Link>
          </label>
        </div>
        {signupTermsError && <p className="tw-text-red-500 tw-text-xs tw-mb-2">{signupTermsError}</p>}

        <button
          type="submit"
          disabled={signupLoading}
          className="tw-w-full tw-py-3 tw-rounded-lg tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-text-sm tw-border-0 tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-mt-2"
        >
          {signupLoading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>
    );
  }

  render() {
    var isOpen = this.context.isOpen;
    var activeTab = this.context.activeTab;
    var showPrimalWelcome = this.state.showPrimalWelcome;
    var confirmationView = this.state.confirmationView;
    var modal = this;

    if (!isOpen && !showPrimalWelcome) return null;

    if (showPrimalWelcome) {
      return this.renderPrimalWelcome();
    }

    // Determine which form to show
    var formContent;
    if (confirmationView) {
      formContent = this.renderConfirmationView();
    } else if (this.state.forgotView) {
      formContent = this.renderForgotView();
    } else if (activeTab === 'login') {
      formContent = this.renderLoginForm();
    } else {
      formContent = this.renderSignupForm();
    }

    return (
      <div
        onMouseDown={this.handleOverlayMouseDown}
        onClick={this.handleOverlayClick}
        className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-p-0 md:tw-p-4"
        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Sign in or create an account"
      >
        <div className="tw-relative tw-w-full tw-h-full md:tw-h-auto md:tw-max-w-md md:tw-rounded-2xl tw-bg-white dark:tw-bg-[#1a1a2e] tw-overflow-y-auto tw-shadow-2xl">
          {/* Close button */}
          <button
            onClick={function() { modal.context.closeAuthModal(); }}
            className="tw-absolute tw-top-4 tw-right-4 tw-z-10 tw-bg-transparent tw-border-0 tw-text-[#999] dark:tw-text-gray-400 hover:tw-text-[#333] dark:hover:tw-text-white tw-cursor-pointer tw-text-xl tw-p-1 tw-transition-colors"
            aria-label="Close"
          >
            <i className="fa fa-times" />
          </button>

          <div className="tw-px-6 tw-py-8 md:tw-px-8">
            {/* Logo */}
            <div className="tw-flex tw-justify-center tw-mb-5">
              <img
                src={this.state.isDark ? loginLogoDark : loginLogoLight}
                alt="Primal Market"
                className="tw-h-14 tw-w-auto"
              />
            </div>

            {/* Tab bar — hidden during forgot password, confirmation views */}
            {!this.state.forgotView && !confirmationView && (
            <div className="tw-flex tw-mb-6 tw-border-b tw-border-[#e0e0e0] dark:tw-border-[#333]">
              <button
                onClick={function() { modal.handleTabSwitch('login'); }}
                className={'tw-flex-1 tw-py-3 tw-text-sm tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-colors tw-bg-transparent ' + (
                  activeTab === 'login'
                    ? 'tw-text-brand-gold tw-border-b-2 tw-border-solid tw-border-brand-gold tw--mb-px'
                    : 'tw-text-[#999] dark:tw-text-gray-400 hover:tw-text-[#666] dark:hover:tw-text-gray-300'
                )}
              >
                Login
              </button>
              <button
                onClick={function() { modal.handleTabSwitch('signup'); }}
                className={'tw-flex-1 tw-py-3 tw-text-sm tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-colors tw-bg-transparent ' + (
                  activeTab === 'signup'
                    ? 'tw-text-brand-gold tw-border-b-2 tw-border-solid tw-border-brand-gold tw--mb-px'
                    : 'tw-text-[#999] dark:tw-text-gray-400 hover:tw-text-[#666] dark:hover:tw-text-gray-300'
                )}
              >
                Sign Up
              </button>
            </div>
            )}

            {/* Forms */}
            {formContent}

            {/* Switch prompt — hidden during forgot password and confirmation views */}
            {!this.state.forgotView && !confirmationView && (
            <p className="tw-text-center tw-text-sm tw-text-[#999] dark:tw-text-gray-400 tw-mt-5 tw-mb-0">
              {activeTab === 'login' ? (
                <span>Don't have an account? <button onClick={function() { modal.handleTabSwitch('signup'); }} className="tw-text-brand-gold hover:tw-text-brand-gold-dark tw-bg-transparent tw-border-0 tw-cursor-pointer tw-font-semibold tw-p-0 tw-text-sm" style={{ marginLeft: '4px' }}>Sign up</button></span>
              ) : (
                <span>Already have an account? <button onClick={function() { modal.handleTabSwitch('login'); }} className="tw-text-brand-gold hover:tw-text-brand-gold-dark tw-bg-transparent tw-border-0 tw-cursor-pointer tw-font-semibold tw-p-0 tw-text-sm" style={{ marginLeft: '4px' }}>Sign in</button></span>
              )}
            </p>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(AuthModal);
